import { useState } from "react";
import axios from "axios";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { COURSES } from "@/data/courses";
import { waLink, SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASSES = [
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class 9",
    "Class 10",
    "Class 11",
    "Class 12",
    "Dropper",
    "Graduate",
];

export default function EnquiryForm({
    defaultCourse = "",
    sourcePage = "homepage",
    variant = "card", // "card" | "inline"
}) {
    const [form, setForm] = useState({
        student_name: "",
        parent_name: "",
        mobile: "",
        student_class: "",
        course: defaultCourse,
        city: SITE.city,
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (
            !form.student_name ||
            !form.parent_name ||
            !form.mobile ||
            !form.student_class ||
            !form.course ||
            !form.city
        ) {
            toast.error("Please fill all required fields");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/enquiries`, {
                ...form,
                source_page: sourcePage,
            });
            toast.success("Enquiry received! Our counsellor will call you within 30 mins.");
            setForm({
                student_name: "",
                parent_name: "",
                mobile: "",
                student_class: "",
                course: defaultCourse,
                city: SITE.city,
                message: "",
            });
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went wrong. Please try WhatsApp.";
            toast.error(typeof msg === "string" ? msg : "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    const wrapperClass =
        variant === "inline"
            ? "bg-white text-brand-ink"
            : "bg-white text-brand-ink border border-black/10 shadow-[0_24px_48px_-24px_rgba(10,25,47,0.25)]";

    return (
        <div data-testid="enquiry-form-wrapper" className={`${wrapperClass} p-6 md:p-8`}>
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="overline text-brand-crimson mb-2">
                        Book Free Demo · No Cost
                    </div>
                    <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-brand-navy leading-tight">
                        Talk to a Counsellor
                    </h3>
                    <p className="text-sm text-brand-sub mt-1">
                        We'll call you back within 30 minutes.
                    </p>
                </div>
                <a
                    href={waLink(`Hi, I want to enquire about ${defaultCourse || "your courses"}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="form-whatsapp-shortcut"
                    className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold text-brand-whatsapp bg-brand-whatsapp/10 px-3 py-2 hover:bg-brand-whatsapp/20 transition-colors"
                >
                    <MessageCircle className="w-3.5 h-3.5" /> Quick WhatsApp
                </a>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="enquiry-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="student_name" className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            Student Name *
                        </Label>
                        <Input
                            id="student_name"
                            data-testid="input-student-name"
                            value={form.student_name}
                            onChange={(e) => update("student_name", e.target.value)}
                            placeholder="Full name"
                            className="rounded-none mt-1.5 border-black/15 focus-visible:ring-brand-navy"
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="parent_name" className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            Parent Name *
                        </Label>
                        <Input
                            id="parent_name"
                            data-testid="input-parent-name"
                            value={form.parent_name}
                            onChange={(e) => update("parent_name", e.target.value)}
                            placeholder="Father / Mother"
                            className="rounded-none mt-1.5 border-black/15 focus-visible:ring-brand-navy"
                            required
                        />
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="mobile" className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            Mobile Number *
                        </Label>
                        <Input
                            id="mobile"
                            data-testid="input-mobile"
                            type="tel"
                            value={form.mobile}
                            onChange={(e) => update("mobile", e.target.value)}
                            placeholder="+91 9876543210"
                            className="rounded-none mt-1.5 border-black/15 focus-visible:ring-brand-navy"
                            required
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            Class *
                        </Label>
                        <Select
                            value={form.student_class}
                            onValueChange={(v) => update("student_class", v)}
                        >
                            <SelectTrigger
                                data-testid="select-class"
                                className="rounded-none mt-1.5 border-black/15"
                            >
                                <SelectValue placeholder="Select class" />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                {CLASSES.map((c) => (
                                    <SelectItem key={c} value={c} className="rounded-none">
                                        {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            Course Interested In *
                        </Label>
                        <Select
                            value={form.course}
                            onValueChange={(v) => update("course", v)}
                        >
                            <SelectTrigger
                                data-testid="select-course"
                                className="rounded-none mt-1.5 border-black/15"
                            >
                                <SelectValue placeholder="Choose a course" />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                {COURSES.map((c) => (
                                    <SelectItem
                                        key={c.slug}
                                        value={c.short}
                                        className="rounded-none"
                                    >
                                        {c.short}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="city" className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                            City *
                        </Label>
                        <Input
                            id="city"
                            data-testid="input-city"
                            value={form.city}
                            onChange={(e) => update("city", e.target.value)}
                            className="rounded-none mt-1.5 border-black/15 focus-visible:ring-brand-navy"
                            required
                        />
                    </div>
                </div>

                <div>
                    <Label htmlFor="message" className="text-xs uppercase tracking-wider font-semibold text-brand-navy">
                        Message (optional)
                    </Label>
                    <Textarea
                        id="message"
                        data-testid="input-message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={3}
                        placeholder="Any specific questions or schedule preferences..."
                        className="rounded-none mt-1.5 border-black/15 focus-visible:ring-brand-navy"
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="enquiry-submit"
                        className="rounded-none flex-1 bg-brand-crimson hover:bg-brand-crimsonHover h-12 font-heading font-bold tracking-wide"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                Submit Enquiry <Send className="w-4 h-4 ml-2" />
                            </>
                        )}
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        data-testid="enquiry-whatsapp"
                        className="rounded-none border-brand-whatsapp text-brand-whatsapp hover:bg-brand-whatsapp hover:text-white h-12 font-heading font-semibold"
                    >
                        <a
                            href={waLink(
                                `Hi, I'm interested in ${form.course || "Dacron Classes"} - my class is ${form.student_class || "..."}`
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Now
                        </a>
                    </Button>
                </div>
                <p className="text-[11px] text-brand-sub mt-2">
                    By submitting, you agree to be contacted by Dacron Classes
                    counsellors. We respect your privacy.
                </p>
            </form>
        </div>
    );
}
